
import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { loadConfig } from "../helpers/config";
import LoadingSpinner from "../components/LoadingSpinner";
import { AuthContext } from "../helpers/AuthContext";

function EditSingleCategory() {
  const { id, idx } = useParams();
  const navigate = useNavigate();
  const { authState } = useContext(AuthContext);
  const [apiUrl, setApiUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState({ name: "", description: "", interval: null, unit: "Monat(e)", customChecks: [] });
  // Store raw dropdown input for each custom check
  const [dropdownInputs, setDropdownInputs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [jsonEditorValue, setJsonEditorValue] = useState("");
  const [jsonEditorError, setJsonEditorError] = useState("");

  useEffect(() => {
    async function fetchConfigAndCategory() {
      const config = await loadConfig();
      setApiUrl(config.APIURL);
      if (idx !== "new") {
        const response = await axios.get(`${config.APIURL}/categories/${idx}`, {
          headers: { accessToken: localStorage.getItem("accessToken") }
        });
        const cat = response.data;
        if (!Array.isArray(cat.customChecks)) cat.customChecks = [];
        setCategory(cat);
        // Initialize dropdownInputs with joined options
        setDropdownInputs(cat.customChecks.map(f => f.type === "dropdown" ? (f.options ? f.options.join(",") : "") : ""));
      } else {
        setDropdownInputs([]);
      }
      setLoading(false);
    }
    fetchConfigAndCategory();
  }, [id, idx]);

  const openJsonEditor = () => {
    setJsonEditorValue(JSON.stringify(category.customChecks || [], null, 2));
    setJsonEditorError("");
    setShowJsonEditor(true);
  };

  const applyJsonEditor = () => {
    try {
      const parsed = JSON.parse(jsonEditorValue);
      if (!Array.isArray(parsed)) {
        setJsonEditorError("Das JSON muss ein Array sein.");
        return;
      }
      setCategory({ ...category, customChecks: parsed });
      setDropdownInputs(parsed.map(f => (f && f.type === "dropdown") ? (Array.isArray(f.options) ? f.options.join(",") : "") : ""));
      setShowJsonEditor(false);
      setJsonEditorError("");
    } catch (e) {
      setJsonEditorError("Ungültiges JSON: " + e.message);
    }
  };

  // Normalized custom check names (trimmed, lower case) and the set of duplicates
  const normalizedCheckNames = category.customChecks.map(f => (f.name || "").trim().toLowerCase());
  const duplicateCheckNames = new Set(
    normalizedCheckNames.filter((name, i) => name && normalizedCheckNames.indexOf(name) !== i)
  );
  const hasDuplicateCheckNames = duplicateCheckNames.size > 0;

  const handleSave = async () => {
    if (hasDuplicateCheckNames) {
      alert("Doppelte Feldnamen sind nicht erlaubt. Bitte vergib jedem Custom Check einen eindeutigen Namen.");
      return;
    }
    setSaving(true);
    // Evaluate dropdownInputs into options arrays before saving
    const updatedCustomChecks = category.customChecks.map((field, idx) => {
      if (field.type === "dropdown") {
        return {
          ...field,
          options: dropdownInputs[idx]
            ? dropdownInputs[idx].split(",").map(opt => opt.trim()).filter(opt => opt)
            : []
        };
      }
      return field;
    });
    const categoryToSave = { ...category, customChecks: updatedCustomChecks };
    try {
      if (idx === "new") {
        await axios.post(`${apiUrl}/categories`, { ...categoryToSave, toolsgroupId: id }, {
          headers: { accessToken: localStorage.getItem("accessToken") }
        });
      } else {
        await axios.put(`${apiUrl}/categories/${idx}`, categoryToSave, {
          headers: { accessToken: localStorage.getItem("accessToken") }
        });
      }
      navigate(`/edit-group-categories/${id}`);
    } catch (err) {
      alert("Fehler beim Speichern der Kategorie.");
    }
    setSaving(false);
  };

  if (loading) return <LoadingSpinner text="Lade Kategorie..." />;

  if (!authState.isAdmin) {
    return (
      <div className="container page-offset-100" style={{ textAlign: 'center' }}>
        <h2>⚠️ Zugriff verweigert</h2>
        <p>Nur Administratoren können Prüfkategorien bearbeiten.</p>
      </div>
    );
  }

  return (
    <div className="container">
      <h2>{idx === "new" ? "Kategorie hinzufügen" : "Kategorie bearbeiten"}</h2>
      <form onSubmit={e => { e.preventDefault(); handleSave(); }}>
        <div className="form-group">
          <label>Name</label>
          <input type="text" className="form-control" value={category.name} onChange={e => setCategory({ ...category, name: e.target.value })} required />
        </div>
        <div className="form-group">
          <label>Beschreibung</label>
          <input type="text" className="form-control" value={category.description} onChange={e => setCategory({ ...category, description: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Intervall</label>
          <input type="number" className="form-control" value={category.interval || ""} onChange={e => setCategory({ ...category, interval: e.target.value ? parseInt(e.target.value) : null })} />
        </div>
        <div className="form-group">
          <label>Einheit</label>
          <select
            className="form-control"
            value={category.unit || "Monat(e)"}
            onChange={e => setCategory({ ...category, unit: e.target.value })}
          >
            <option value="Monat(e)">Monat(e)</option>
          </select>
        </div>
        <hr />
        <h4>Custom Checks für diese Kategorie</h4>
        <button type="button" className="btn btn-secondary" style={{ marginBottom: "12px" }} onClick={openJsonEditor}>JSON bearbeiten</button>
        {category.customChecks.length === 0 && <p>Keine Custom Checks definiert.</p>}
        <ul className="field-editor-list">
          {category.customChecks.map((field, idx) => (
            <li key={idx} className="field-editor-item">
              <div className="form-group">
                <label>Feldname</label>
                <input value={field.name} onChange={e => {
                  const updated = [...category.customChecks];
                  updated[idx].name = e.target.value;
                  setCategory({ ...category, customChecks: updated });
                }} placeholder="z.B. Batterie" className={`form-control${duplicateCheckNames.has((field.name || "").trim().toLowerCase()) ? " is-invalid" : ""}`} />
                {duplicateCheckNames.has((field.name || "").trim().toLowerCase()) && (
                  <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "4px" }}>
                    Dieser Feldname wird bereits verwendet.
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Typ</label>
                <select value={field.type} onChange={e => {
                  const updated = [...category.customChecks];
                  updated[idx].type = e.target.value;
                  setCategory({ ...category, customChecks: updated });
                }} className="form-control">
                  <option value="checkbox">Checkbox</option>
                  <option value="dropdown">Dropdown</option>
                  <option value="number">Nummer</option>
                  <option value="text">Text</option>
                </select>
              </div>
              {field.type === "dropdown" && (
                <div className="form-group">
                  <label>Dropdown Optionen (Komma getrennt)</label>
                  <input
                    value={dropdownInputs[idx] || ""}
                    onChange={e => {
                      const newInputs = [...dropdownInputs];
                      newInputs[idx] = e.target.value;
                      setDropdownInputs(newInputs);
                    }}
                    placeholder="z.B. Option1, Option2, Option3"
                    className="form-control"
                  />
                </div>
              )}
              <div className="form-group">
                <label className="required-checkbox-label">
                  <input type="checkbox" checked={field.required || false} onChange={e => {
                    const updated = [...category.customChecks];
                    updated[idx].required = e.target.checked;
                    setCategory({ ...category, customChecks: updated });
                  }} /> Pflichtfeld
                </label>
              </div>
              <button type="button" className="btn btn-secondary" onClick={() => {
                setCategory({ ...category, customChecks: category.customChecks.filter((_, i) => i !== idx) });
              }}>Entfernen</button>
            </li>
          ))}
        </ul>
        <button type="button" className="btn btn-primary" style={{ marginBottom: "16px" }} onClick={() => {
          setCategory({ ...category, customChecks: [...category.customChecks, { name: "", type: "checkbox", required: false, options: [] }] });
        }}>Custom Check hinzufügen</button>
        {hasDuplicateCheckNames && (
          <div style={{ color: "#dc3545", fontSize: "0.875rem", marginBottom: "8px" }}>
            Es gibt Custom Checks mit gleichem Feldnamen. Bitte vergib eindeutige Namen, um zu speichern.
          </div>
        )}
        <button type="submit" className="btn btn-primary" disabled={saving || hasDuplicateCheckNames}>Speichern</button>
        <button type="button" className="btn btn-secondary" style={{ marginLeft: "8px" }} onClick={() => navigate(`/edit-group-categories/${id}`)}>Abbrechen</button>
      </form>

      {/* JSON Editor Modal */}
      {showJsonEditor && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="json-editor-title"
          onKeyDown={e => { if (e.key === "Escape") setShowJsonEditor(false); }}
          tabIndex={-1}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}>
          <div style={{
            background: "#fff",
            borderRadius: "8px",
            padding: "28px 24px 20px 24px",
            width: "min(640px, 95vw)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
            display: "flex",
            flexDirection: "column",
            gap: "12px"
          }}>
            <h5 id="json-editor-title" style={{ marginBottom: "4px" }}>Custom Checks – JSON bearbeiten</h5>
            <p style={{ fontSize: "0.875rem", color: "#555", margin: 0 }}>
              Bearbeite die Custom Checks direkt als JSON-Array. Klicke auf „Übernehmen", um die Änderungen zu speichern.
            </p>
            <textarea
              value={jsonEditorValue}
              onChange={e => { setJsonEditorValue(e.target.value); setJsonEditorError(""); }}
              style={{
                width: "100%",
                minHeight: "280px",
                fontFamily: "monospace",
                fontSize: "0.85rem",
                padding: "10px",
                border: jsonEditorError ? "2px solid #dc3545" : "1px solid #ccc",
                borderRadius: "4px",
                resize: "vertical"
              }}
              spellCheck={false}
            />
            {jsonEditorError && (
              <div style={{ color: "#dc3545", fontSize: "0.875rem" }}>{jsonEditorError}</div>
            )}
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowJsonEditor(false)}>Abbrechen</button>
              <button type="button" className="btn btn-primary" onClick={applyJsonEditor}>Übernehmen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditSingleCategory;
